/**
 * Colorado State-Specific Diagnostic Questions
 * All questions reference actual CO regulations, forms, and requirements
 * 
 * Regulatory Body: Colorado Department of Public Health and Environment (CDPHE)
 * Primary Regulation: 6 CCR 1011-1 Chapter 26 - Home Care Agencies
 * Latest Update: Effective July 1, 2024 (Board of Health April 17, 2024)
 * 
 * REGULATORY STRUCTURE:
 * - Part 1: Statutory Authority and Applicability
 * - Part 2: Definitions
 * - Part 3: License Categories (Class A = Skilled, Class B = Personal Care Only)
 * - Part 4: Department Oversight (Licensing, Fees, Insurance, Enforcement)
 * - Part 5: General Requirements for All License Categories
 * - Part 6: Additional Requirements for Class A Agencies
 * - Part 7: Non-Medical/Personal Care Requirements
 * - Part 8: Home Care Placement Agencies
 * 
 * LICENSE CLASSES:
 * - Class A: Provides skilled healthcare services (may also provide personal care)
 * - Class B: Provides only personal care services (no skilled healthcare)
 * 
 * INSURANCE REQUIREMENTS (Part 4.2):
 * - Class A: $500,000 per occurrence / $3,000,000 aggregate
 * - Class B: $100,000 per occurrence / $300,000 aggregate
 * - Surety bond may be used IN LIEU OF liability insurance
 */

import type { DiagnosticQuestionConfig } from '../../../types/diagnostic';

export const COLORADO_QUESTIONS: DiagnosticQuestionConfig[] = [
  // ============================================
  // SECTION A: LICENSING & OPERATIONS
  // ============================================
  {
    id: 'CO-A1',
    itemId: 'A1',
    stateCode: 'CO',
    title: 'Home Care Agency License Status',
    description: 'Colorado requires all home care agencies to be licensed by CDPHE under 6 CCR 1011-1 Chapter 26',
    severity: 'high',
    regulationCode: '6 CCR 1011-1',
    regulationName: 'Licensure',
    agencyName: 'Colorado Department of Public Health and Environment',
    agencyAbbr: 'CDPHE',
    agencyPhone: '(303) 692-2800',
    agencyUrl: 'https://cdphe.colorado.gov/home-care-agencies',
    questions: [
      {
        id: 'q1',
        question: 'Do you have a current, active Home Care Agency license from the Colorado Department of Public Health and Environment?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        required: true,
        helpText: 'Home care agency licenses must be renewed annually. Check your license status with CDPHE.',
      },
      {
        id: 'q2',
        question: 'What class of license do you hold?',
        inputType: 'select',
        options: [
          { value: 'class_a', label: 'Class A (Skilled healthcare services)' },
          { value: 'class_b', label: 'Class B (Personal care services only)' },
          { value: 'unsure', label: 'Not sure' },
        ],
        helpText: 'Class A agencies may provide skilled nursing and personal care. Class B agencies provide only personal care services.',
        showCondition: {
          questionId: 'q1',
          operator: 'equals',
          value: 'yes',
        },
      },
      {
        id: 'q3',
        question: 'What is your license expiration date?',
        inputType: 'date',
        helpText: 'Found on your CDPHE license certificate. Colorado licenses are renewed annually.',
        showCondition: {
          questionId: 'q1',
          operator: 'equals',
          value: 'yes',
        },
      },
    ],
    scoringRules: [
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
      { condition: 'equals', questionId: 'q2', value: 'unsure', result: 'AT_RISK' },
      { condition: 'daysFuture', questionId: 'q3', value: 90, result: 'COMPLIANT' },
      { condition: 'daysFuture', questionId: 'q3', value: 60, result: 'AT_RISK' },
      { condition: 'daysFuture', questionId: 'q3', value: 0, result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Your Colorado Department of Public Health and Environment Home Care Agency license is current and active per 6 CCR 1011-1 Chapter 26, Part 4.1.',
      atRisk: 'Your license expires within 90 days. Colorado requires renewal applications to be submitted to CDPHE before expiration. Submit your renewal application immediately.',
      nonCompliant: 'Operating a home care agency in Colorado without a valid CDPHE license violates C.R.S. 25-27.5-103 and can result in civil penalties up to $10,000 per calendar year for all violations. Contact CDPHE immediately at (303) 692-2800.',
      unknown: 'Verify your license status with CDPHE at (303) 692-2800.',
    },
    stateNotes: [
      'Home care agency licenses are renewed annually',
      'Class A agencies provide skilled healthcare services and may also provide personal care',
      'Class B agencies provide only personal care services - no skilled healthcare',
      'License fees vary based on type, volume of services, and Medicaid/Medicare participation',
      'CDPHE may conduct unannounced surveys based on compliance history',
    ],
  },

  {
    id: 'CO-A2',
    itemId: 'A2',
    stateCode: 'CO',
    title: 'Agency Administrator/Manager Qualifications',
    description: 'Colorado requires qualified administrator oversight per 6 CCR 1011-1 Chapter 26, Part 7.1',
    severity: 'high',
    regulationCode: '6 CCR 1011-1',
    regulationName: 'Manager',
    agencyName: 'Colorado Department of Public Health and Environment',
    agencyAbbr: 'CDPHE',
    agencyPhone: '(303) 692-2800',
    agencyUrl: 'https://cdphe.colorado.gov/home-care-agencies',
    questions: [
      {
        id: 'q1',
        question: 'Does your agency have a designated manager/administrator?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        required: true,
        helpText: 'Per Part 7.1, a manager controls and supervises day-to-day operations',
      },
      {
        id: 'q2',
        question: 'Has your manager completed the required 8-hour CDPHE-approved manager training program?',
        inputType: 'select',
        options: [
          { value: 'yes_approved', label: 'Yes, completed CDPHE-approved 8-hour training' },
          { value: 'partial', label: 'Completed some training, not full 8 hours' },
          { value: 'no', label: 'No manager training completed' },
        ],
        required: true,
        helpText: 'Part 7.1(D) requires managers to complete an 8-hour training program from an accredited source',
      },
      {
        id: 'q3',
        question: 'Is your administrator available during business hours to manage agency operations?',
        inputType: 'select',
        options: [
          { value: 'fulltime', label: 'Yes, full-time availability' },
          { value: 'parttime', label: 'Part-time availability' },
          { value: 'no', label: 'No designated availability' },
        ],
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'yes' },
        { condition: 'equals', questionId: 'q2', value: 'yes_approved' },
        { condition: 'equals', questionId: 'q3', value: 'fulltime' },
      ], result: 'COMPLIANT' },
      { condition: 'anyTrue', value: [
        { condition: 'equals', questionId: 'q2', value: 'partial' },
        { condition: 'equals', questionId: 'q3', value: 'parttime' },
      ], result: 'AT_RISK' },
      { condition: 'anyTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'no' },
        { condition: 'equals', questionId: 'q2', value: 'no' },
      ], result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Your agency has a qualified manager who completed the required 8-hour training per 6 CCR 1011-1 Chapter 26, Part 7.1(D).',
      atRisk: 'Manager training or availability may not meet Colorado requirements. Part 7.1(D) requires an 8-hour CDPHE-approved training program.',
      nonCompliant: 'Operating without a qualified manager violates 6 CCR 1011-1 Chapter 26, Part 7.1. Contact CDPHE at (303) 692-2800.',
      unknown: 'Verify manager qualifications with CDPHE.',
    },
    stateNotes: [
      'Manager training must be at least 8 actual hours',
      'Training must be from an accredited college, university, vocational school, or organization with specific expertise',
      'Required topics include: home care overview, regulatory compliance, personnel qualifications, needs of fragile populations',
      'Training documentation must be maintained in personnel file',
    ],
  },

  {
    id: 'CO-A3',
    itemId: 'A3',
    stateCode: 'CO',
    title: 'Insurance Requirements by License Class',
    description: 'Colorado requires specific insurance coverage based on license class (A or B)',
    severity: 'high',
    regulationCode: '6 CCR 1011-1',
    regulationName: 'Insurance',
    agencyName: 'Colorado Department of Public Health and Environment',
    agencyAbbr: 'CDPHE',
    agencyPhone: '(303) 692-2800',
    agencyUrl: 'https://cdphe.colorado.gov/home-care-agencies',
    questions: [
      {
        id: 'q1',
        question: 'What class of license does your agency hold?',
        inputType: 'select',
        options: [
          { value: 'class_a', label: 'Class A (Skilled healthcare services)' },
          { value: 'class_b', label: 'Class B (Personal care services only)' },
        ],
        required: true,
        helpText: 'Insurance requirements differ by license class',
      },
      {
        id: 'q2',
        question: 'What is your general/professional liability insurance coverage per occurrence?',
        inputType: 'number',
        required: true,
        helpText: 'Class A requires $500,000 minimum. Class B requires $100,000 minimum.',
      },
      {
        id: 'q3',
        question: 'What is your liability insurance aggregate coverage?',
        inputType: 'number',
        required: true,
        helpText: 'Class A requires $3,000,000 aggregate. Class B requires $300,000 aggregate.',
      },
      {
        id: 'q4',
        question: 'Do you maintain workers\' compensation insurance?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        required: true,
        helpText: 'Workers\' compensation is required per C.R.S. 8-40-101 et seq.',
      },
      {
        id: 'q5',
        question: 'Do you use a surety bond IN LIEU OF liability insurance?',
        inputType: 'select',
        options: [
          { value: 'no_bond', label: 'No - we have liability insurance' },
          { value: 'bond_only', label: 'Yes - surety bond instead of liability insurance' },
          { value: 'both', label: 'Yes - we have both liability insurance and surety bond' },
        ],
        helpText: 'Part 4.2(B) allows a surety bond as an alternative to liability insurance',
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'class_a' },
        { condition: 'greaterOrEqual', questionId: 'q2', value: 500000 },
        { condition: 'greaterOrEqual', questionId: 'q3', value: 3000000 },
        { condition: 'equals', questionId: 'q4', value: 'yes' },
      ], result: 'COMPLIANT' },
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'class_b' },
        { condition: 'greaterOrEqual', questionId: 'q2', value: 100000 },
        { condition: 'greaterOrEqual', questionId: 'q3', value: 300000 },
        { condition: 'equals', questionId: 'q4', value: 'yes' },
      ], result: 'COMPLIANT' },
      { condition: 'anyTrue', value: [
        { condition: 'equals', questionId: 'q4', value: 'no' },
      ], result: 'NON_COMPLIANT' },
      // NON_COMPLIANT: Class A with insufficient per-occurrence coverage
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'class_a' },
        { condition: 'lessThan', questionId: 'q2', value: 500000 },
      ], result: 'NON_COMPLIANT' },
      // NON_COMPLIANT: Class B with insufficient per-occurrence coverage
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'class_b' },
        { condition: 'lessThan', questionId: 'q2', value: 100000 },
      ], result: 'NON_COMPLIANT' },
      // Surety bond in lieu of liability insurance per Part 4.2(B) - inform but don't penalize
      { condition: 'equals', questionId: 'q5', value: 'bond_only', result: 'AT_RISK' },
    ],
    reportLanguage: {
      compliant: 'Your insurance coverage meets Colorado requirements per 6 CCR 1011-1 Chapter 26, Part 4.2(B).',
      atRisk: 'Your insurance may not meet all Colorado requirements. If using surety bond in lieu of liability insurance, verify bond meets Part 4.2(B) requirements.',
      nonCompliant: 'Your insurance coverage does not meet Colorado requirements. Class A requires $500,000/$3,000,000. Class B requires $100,000/$300,000. Contact CDPHE at (303) 692-2800.',
      unknown: 'Verify your insurance meets Part 4.2(B) requirements for your license class.',
    },
    stateNotes: [
      'Class A (skilled care): $500,000 per occurrence / $3,000,000 aggregate',
      'Class B (personal care only): $100,000 per occurrence / $300,000 aggregate',
      'Surety bond may be used IN LIEU OF (not in addition to) liability insurance',
      'Workers\' compensation is separately required under Colorado labor law',
      'Insurance must be maintained for duration of license period',
    ],
  },

  {
    id: 'CO-A4',
    itemId: 'A4',
    stateCode: 'CO',
    title: 'Written Policies and Procedures',
    description: 'Colorado requires specific written policies per 6 CCR 1011-1 Chapter 26, Part 5',
    severity: 'medium',
    regulationCode: '6 CCR 1011-1',
    regulationName: 'Policies',
    agencyName: 'Colorado Department of Public Health and Environment',
    agencyAbbr: 'CDPHE',
    agencyPhone: '(303) 692-2800',
    agencyUrl: 'https://cdphe.colorado.gov/home-care-agencies',
    questions: [
      {
        id: 'q1',
        question: 'Which of the following Colorado-required policies does your agency have documented? (Select all that apply)',
        inputType: 'checklist',
        options: [
          { value: 'consumer_rights', label: 'Consumer rights and responsibilities (Part 5.3)' },
          { value: 'complaint', label: 'Complaint investigation and resolution (Part 5.4)' },
          { value: 'quality', label: 'Quality management program (Part 5.5)' },
          { value: 'infection', label: 'Infection prevention and control (Part 5.6)' },
          { value: 'personnel_health', label: 'Personnel health and communicable disease (Part 5.6.C)' },
          { value: 'personnel_files', label: 'Personnel file requirements (Part 5.7)' },
          { value: 'background', label: 'Background check procedures (Part 5.7.B-E)' },
          { value: 'abuse_reporting', label: 'Abuse/neglect reporting - APS (C.R.S. 26-3.1-102)' },
          { value: 'emergency', label: 'Emergency and disaster preparedness' },
          { value: 'occurrence', label: 'Occurrence reporting (6 CCR 1011-1 Chapter 2, Part 4.2)' },
        ],
        required: true,
      },
    ],
    scoringRules: [
      { condition: 'countAtLeast', questionId: 'q1', value: 10, result: 'COMPLIANT' },
      { condition: 'countAtLeast', questionId: 'q1', value: 7, result: 'AT_RISK' },
      { condition: 'countLessThan', questionId: 'q1', value: 7, result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Your policy manual meets Colorado 6 CCR 1011-1 Chapter 26, Part 5 requirements.',
      atRisk: 'Your policy manual is missing required sections per Part 5. CDPHE surveyors will review policies for completeness. Missing policies: {missing}.',
      nonCompliant: 'Multiple required policies are missing per 6 CCR 1011-1 Chapter 26, Part 5. This is a common CDPHE survey deficiency. Immediately develop written policies for: {missing}.',
      unknown: 'Review your policies against Part 5 requirements.',
    },
    stateNotes: [
      'Part 5 establishes general requirements for all license categories',
      'Policies must be documented in writing and available for CDPHE review',
      'Abuse/neglect reporting policy must reference C.R.S. 26-3.1-102',
      'CDPHE surveys commonly cite incomplete policy manuals',
    ],
  },

  {
    id: 'CO-A5',
    itemId: 'A5',
    stateCode: 'CO',
    title: 'Consumer Rights Notification',
    description: 'Colorado requires written consumer rights disclosure per Part 5.3',
    severity: 'high',
    regulationCode: '6 CCR 1011-1',
    regulationName: 'Consumer Rights',
    agencyName: 'Colorado Department of Public Health and Environment',
    agencyAbbr: 'CDPHE',
    agencyPhone: '(303) 692-2800',
    agencyUrl: 'https://cdphe.colorado.gov/home-care-agencies',
    questions: [
      {
        id: 'q1',
        question: 'Do you provide ALL consumers with a written statement of their rights before services begin?',
        inputType: 'select',
        options: [
          { value: 'always', label: 'Yes, before services begin' },
          { value: 'sometimes', label: 'Sometimes, not consistently' },
          { value: 'no', label: 'No' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'Which consumer rights are included in your written statement? (Select all that apply)',
        inputType: 'checklist',
        options: [
          { value: 'participate', label: 'Right to participate in planning care' },
          { value: 'informed', label: 'Right to be informed of services, charges, and provider info' },
          { value: 'staff_info', label: 'Right to know staff name, licensure status, employer' },
          { value: 'competent', label: 'Right to be served by trained and competent staff' },
          { value: 'free_restraint', label: 'Right to be free from restraints' },
          { value: 'complaints', label: 'Right to express complaints without retaliation' },
          { value: 'privacy', label: 'Right to privacy and confidentiality' },
          { value: 'refuse', label: 'Right to refuse services' },
        ],
        helpText: 'Part 5.3 specifies required consumer rights disclosures',
      },
      {
        id: 'q3',
        question: 'Do consumers sign acknowledgment of receiving their rights?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'always' },
        { condition: 'countAtLeast', questionId: 'q2', value: 7 },
        { condition: 'equals', questionId: 'q3', value: 'yes' },
      ], result: 'COMPLIANT' },
      { condition: 'anyTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'sometimes' },
        { condition: 'countAtLeast', questionId: 'q2', value: 4 },
      ], result: 'AT_RISK' },
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
      { condition: 'countLessThan', questionId: 'q1', value: 1, result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Consumer rights notification process meets 6 CCR 1011-1 Chapter 26, Part 5.3 requirements.',
      atRisk: 'Consumer rights notification is inconsistent or incomplete. Ensure all consumers receive written rights before services begin.',
      nonCompliant: 'Failure to provide consumer rights violates Part 5.3. Implement immediate corrective action.',
      unknown: 'Review consumer rights notification process.',
    },
    stateNotes: [
      'Written statement must be provided BEFORE services begin per Part 5.3',
      'Consumer must sign acknowledgment of receipt',
      'Rights include participation in care planning, refusal of services, privacy',
      'Part 5.3 specifies all required consumer rights disclosures',
    ],
  },

  {
    id: 'CO-A6',
    itemId: 'A6',
    stateCode: 'CO',
    title: 'Disclosure Notice Requirements',
    description: 'Colorado requires written disclosure notice within 1 business day of service start',
    severity: 'high',
    regulationCode: '6 CCR 1011-1',
    regulationName: 'Disclosure Notice',
    agencyName: 'Colorado Department of Public Health and Environment',
    agencyAbbr: 'CDPHE',
    agencyPhone: '(303) 692-2800',
    agencyUrl: 'https://cdphe.colorado.gov/home-care-agencies',
    questions: [
      {
        id: 'q1',
        question: 'Do you provide a written disclosure notice within 1 business day of the start of services?',
        inputType: 'select',
        options: [
          { value: 'always', label: 'Yes, always within 1 business day' },
          { value: 'usually', label: 'Usually, but sometimes later' },
          { value: 'no', label: 'No disclosure notice provided' },
        ],
        required: true,
        helpText: 'Part 5.8 requires disclosure notice in CDPHE-prescribed form',
      },
      {
        id: 'q2',
        question: 'Does your disclosure notice include responsibility for the following? (Select all that apply)',
        inputType: 'checklist',
        options: [
          { value: 'unemployment', label: 'Unemployment insurance' },
          { value: 'workers_comp', label: 'Workers\' compensation' },
          { value: 'liability', label: 'General liability insurance' },
          { value: 'bond', label: 'Bond insurance (if provided)' },
          { value: 'materials', label: 'Materials/supplies for worker use' },
          { value: 'training', label: 'Training and qualifications' },
        ],
        helpText: 'Part 5.8(2) specifies required disclosure elements',
      },
      {
        id: 'q3',
        question: 'Is the disclosure notice signed by the consumer or authorized representative?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'always' },
        { condition: 'countAtLeast', questionId: 'q2', value: 5 },
        { condition: 'equals', questionId: 'q3', value: 'yes' },
      ], result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q1', value: 'usually', result: 'AT_RISK' },
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
      { condition: 'countLessThan', questionId: 'q1', value: 1, result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Disclosure notice process meets 6 CCR 1011-1 Chapter 26, Part 5.8 requirements.',
      atRisk: 'Disclosure notices are not consistently provided within 1 business day. Improve timeliness.',
      nonCompliant: 'Failure to provide disclosure notice violates Part 5.8. Implement CDPHE-prescribed form immediately.',
      unknown: 'Review disclosure notice requirements.',
    },
    stateNotes: [
      'Disclosure notice required within 1 business day of service start per Part 5.8',
      'Must use CDPHE-prescribed form',
      'Must be signed by consumer or authorized representative',
      'Part 5.8(2) specifies required disclosure elements including insurance responsibilities',
    ],
  },

  {
    id: 'CO-A7',
    itemId: 'A7',
    stateCode: 'CO',
    title: 'Quality Management Program',
    description: 'Colorado requires quality management program per Part 5.5',
    severity: 'medium',
    regulationCode: '6 CCR 1011-1',
    regulationName: 'Quality Management',
    agencyName: 'Colorado Department of Public Health and Environment',
    agencyAbbr: 'CDPHE',
    agencyPhone: '(303) 692-2800',
    agencyUrl: 'https://cdphe.colorado.gov/home-care-agencies',
    questions: [
      {
        id: 'q1',
        question: 'Does your agency have a formal quality management program?',
        inputType: 'select',
        options: [
          { value: 'comprehensive', label: 'Yes, comprehensive program appropriate to agency size' },
          { value: 'basic', label: 'Yes, basic program in place' },
          { value: 'no', label: 'No formal QA program' },
        ],
        required: true,
        helpText: 'Part 5.5 requires QM program that evaluates quality of consumer services, care, and safety',
      },
      {
        id: 'q2',
        question: 'Does your quality management program comply with 6 CCR 1011-1 Chapter 2, Part 4.1?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No / Not sure' },
        ],
        helpText: 'Part 5.5 references Chapter 2, Part 4.1 requirements',
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'comprehensive' },
        { condition: 'equals', questionId: 'q2', value: 'yes' },
      ], result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q1', value: 'basic', result: 'AT_RISK' },
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
      { condition: 'equals', questionId: 'q2', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Quality management program meets 6 CCR 1011-1 Chapter 26, Part 5.5 requirements.',
      atRisk: 'Quality management program may need enhancement. Ensure compliance with Chapter 2, Part 4.1.',
      nonCompliant: 'No quality management program exists. This violates Part 5.5. Implement QM program immediately.',
      unknown: 'Review quality management requirements.',
    },
    stateNotes: [
      'QM program must be appropriate to agency size per Part 5.5',
      'Must evaluate quality of consumer services, care, and safety',
      'Must comply with Chapter 2, Part 4.1 requirements',
      'CDPHE surveys review QM documentation and effectiveness',
    ],
  },

  {
    id: 'CO-A8',
    itemId: 'A8',
    stateCode: 'CO',
    title: 'Complaint Investigation Process',
    description: 'Colorado requires formal complaint investigation and resolution process',
    severity: 'medium',
    regulationCode: '6 CCR 1011-1',
    regulationName: 'Complaint Resolution',
    agencyName: 'Colorado Department of Public Health and Environment',
    agencyAbbr: 'CDPHE',
    agencyPhone: '(303) 692-2800',
    agencyUrl: 'https://cdphe.colorado.gov/home-care-agencies',
    questions: [
      {
        id: 'q1',
        question: 'Do you have a formal complaint investigation and resolution process?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'Which elements does your complaint process include? (Select all that apply)',
        inputType: 'checklist',
        options: [
          { value: 'notify', label: 'Notify complainant of investigation results' },
          { value: 'plan', label: 'Plan to resolve identified issues' },
          { value: 'qa_incorporate', label: 'Incorporate findings into QA program' },
          { value: 'no_retaliation', label: 'Explicit non-retaliation statement' },
          { value: 'record', label: 'Maintain written complaint record/log' },
          { value: 'retention', label: 'Retain records at least 2 years' },
        ],
        helpText: 'Part 5.4 specifies required complaint process elements',
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'yes' },
        { condition: 'countAtLeast', questionId: 'q2', value: 5 },
      ], result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q1', value: 'yes', result: 'AT_RISK' },
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
      { condition: 'countLessThan', questionId: 'q1', value: 1, result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Complaint process meets 6 CCR 1011-1 Chapter 26, Part 5.4 requirements.',
      atRisk: 'Complaint process may be incomplete. Ensure all required elements per Part 5.4.',
      nonCompliant: 'No complaint process exists. This violates Part 5.4. Implement process immediately.',
      unknown: 'Review complaint process requirements.',
    },
    stateNotes: [
      'Must notify complainant of investigation results and resolution plan',
      'Incorporate findings into QA program for systemic changes',
      'Explicit statement that agency does not discriminate or retaliate',
      'Maintain separate complaint record/log for at least 2 years',
      'Records must be available for audit and inspection',
    ],
  },

  {
    id: 'CO-A9',
    itemId: 'A9',
    stateCode: 'CO',
    title: 'Business Entity Registration',
    description: 'Colorado requires proper business registration with Secretary of State',
    severity: 'high',
    regulationCode: 'C.R.S. 7-90-301',
    regulationName: 'Business Registration',
    agencyName: 'Colorado Secretary of State',
    agencyAbbr: 'SOS',
    agencyPhone: '(303) 894-2200',
    agencyUrl: 'https://www.sos.state.co.us/biz/',
    questions: [
      {
        id: 'q1',
        question: 'Is your agency registered as a business entity with the Colorado Secretary of State?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'Is your business registration in good standing?',
        inputType: 'select',
        options: [
          { value: 'good', label: 'Yes, in good standing' },
          { value: 'delinquent', label: 'Delinquent - periodic report overdue' },
          { value: 'unknown', label: 'Unknown / Not sure' },
        ],
        showCondition: {
          questionId: 'q1',
          operator: 'equals',
          value: 'yes',
        },
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'yes' },
        { condition: 'equals', questionId: 'q2', value: 'good' },
      ], result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q2', value: 'delinquent', result: 'AT_RISK' },
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Business entity is registered and in good standing with Colorado Secretary of State.',
      atRisk: 'Business registration is delinquent. File your periodic report at sos.state.co.us to avoid administrative dissolution.',
      nonCompliant: 'Operating without proper business registration. Register with Colorado Secretary of State at (303) 894-2200.',
      unknown: 'Verify business registration status at sos.state.co.us.',
    },
    stateNotes: [
      'Business registration required per C.R.S. 7-90-301',
      'Periodic reports required annually to maintain good standing',
      'Delinquent status can lead to administrative dissolution',
      'Verify status at sos.state.co.us',
    ],
  },

  {
    id: 'CO-A10',
    itemId: 'A10',
    stateCode: 'CO',
    title: 'Occurrence Reporting',
    description: 'Colorado requires occurrence reporting per Chapter 2, Part 4.2',
    severity: 'high',
    regulationCode: '6 CCR 1011-1',
    regulationName: 'Occurrence Reporting',
    agencyName: 'Colorado Department of Public Health and Environment',
    agencyAbbr: 'CDPHE',
    agencyPhone: '(303) 692-2800',
    agencyUrl: 'https://cdphe.colorado.gov/home-care-agencies',
    questions: [
      {
        id: 'q1',
        question: 'Do you have a process for reporting occurrences to CDPHE as required?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        required: true,
        helpText: 'Part 5.6(D) references Chapter 2, Part 4.2 occurrence reporting requirements',
      },
      {
        id: 'q2',
        question: 'Do you track and report required occurrences including abuse/neglect allegations?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'yes' },
        { condition: 'equals', questionId: 'q2', value: 'yes' },
      ], result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q1', value: 'yes', result: 'AT_RISK' },
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
      { condition: 'equals', questionId: 'q2', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Occurrence reporting process meets Colorado requirements.',
      atRisk: 'Occurrence reporting may not capture all required events. Review Chapter 2, Part 4.2 requirements.',
      nonCompliant: 'No occurrence reporting process. This violates Colorado regulations.',
      unknown: 'Review occurrence reporting requirements.',
    },
    stateNotes: [
      'Part 5.6(D) references Chapter 2, Part 4.2 occurrence reporting requirements',
      'Must track and report abuse/neglect allegations',
      'Timely reporting required to CDPHE',
      'Failure to report can result in licensing action',
    ],
  },

  // ============================================
  // SECTION B: PERSONNEL FILES
  // ============================================
  {
    id: 'CO-B1',
    itemId: 'B1',
    stateCode: 'CO',
    title: 'Personnel File Requirements',
    description: 'Colorado requires specific documents in each worker\'s personnel file per Part 5.7',
    severity: 'high',
    regulationCode: '6 CCR 1011-1',
    regulationName: 'Personnel',
    agencyName: 'Colorado Department of Public Health and Environment',
    agencyAbbr: 'CDPHE',
    agencyPhone: '(303) 692-2800',
    agencyUrl: 'https://cdphe.colorado.gov/home-care-agencies',
    questions: [
      {
        id: 'q1',
        question: 'Which of the following documents do you maintain in EVERY home care worker\'s personnel file? (Select all that apply)',
        inputType: 'checklist',
        options: [
          { value: 'app', label: 'Employment application' },
          { value: 'id', label: 'Government-issued photo ID' },
          { value: 'cbi', label: 'CBI background check (within 90 days prior to employment)' },
          { value: 'caps', label: 'CAPS Check (CO Adult Protective Services Data System)' },
          { value: 'dora', label: 'DORA license verification (for licensed staff)' },
          { value: 'tb', label: 'TB clearance documentation' },
          { value: 'i9', label: 'I-9 Employment Eligibility Verification' },
          { value: 'w4', label: 'W-4 tax forms' },
          { value: 'training', label: 'Training and competency documentation' },
          { value: 'job_desc', label: 'Signed job description' },
        ],
        required: true,
      },
    ],
    scoringRules: [
      { condition: 'countAtLeast', questionId: 'q1', value: 10, result: 'COMPLIANT' },
      { condition: 'countAtLeast', questionId: 'q1', value: 7, result: 'AT_RISK' },
      { condition: 'countLessThan', questionId: 'q1', value: 7, result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Personnel files contain all Colorado-required documentation per 6 CCR 1011-1 Chapter 26, Part 5.7.',
      atRisk: 'Some personnel files are missing required documents. Colorado requires: CBI check (within 90 days prior to employment), CAPS Check, DORA verification, and training records. Missing: {missing}.',
      nonCompliant: 'Personnel files are missing critical documentation required by Part 5.7. CDPHE surveyors will cite incomplete files. Immediately obtain: {missing}.',
      unknown: 'Audit personnel files against Part 5.7 requirements.',
    },
    stateNotes: [
      'Part 5.7 specifies all required personnel file documents',
      'CBI background check must be within 90 days prior to employment',
      'CAPS Check required for all direct care workers',
      'DORA verification required for licensed professionals',
      'Training and competency documentation must be maintained',
    ],
  },

  {
    id: 'CO-B2',
    itemId: 'B2',
    stateCode: 'CO',
    title: 'Personal Care Worker Qualifications',
    description: 'Colorado requires specific qualifications for personal care workers per Part 7.2',
    severity: 'high',
    regulationCode: '6 CCR 1011-1',
    regulationName: 'Worker Qualifications',
    agencyName: 'Colorado Department of Public Health and Environment',
    agencyAbbr: 'CDPHE',
    agencyPhone: '(303) 692-2800',
    agencyUrl: 'https://cdphe.colorado.gov/home-care-agencies',
    questions: [
      {
        id: 'q1',
        question: 'Do ALL your personal care workers meet Colorado qualification requirements? (Select all that apply)',
        inputType: 'checklist',
        options: [
          { value: 'age', label: 'At least 18 years old' },
          { value: 'background', label: 'Clear background check (CBI within 90 days prior)' },
          { value: 'caps', label: 'CAPS Check cleared' },
          { value: 'training', label: 'Completed required initial training' },
          { value: 'competency', label: 'Passed competency evaluation' },
          { value: 'health', label: 'Health screening completed (TB, communicable disease)' },
        ],
        required: true,
      },
    ],
    scoringRules: [
      { condition: 'countAtLeast', questionId: 'q1', value: 6, result: 'COMPLIANT' },
      { condition: 'countAtLeast', questionId: 'q1', value: 4, result: 'AT_RISK' },
      { condition: 'countLessThan', questionId: 'q1', value: 4, result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'All personal care workers meet Colorado qualification requirements per Part 7.2.',
      atRisk: 'Some workers may not meet all qualification requirements. Review worker credentials immediately.',
      nonCompliant: 'Workers are employed who do not meet Colorado qualification requirements per Part 7.2.',
      unknown: 'Verify all worker qualifications against Part 7.2.',
    },
    stateNotes: [
      'Workers must be at least 18 years old per Part 7.2',
      'Clear CBI background check required within 90 days prior',
      'CAPS Check must be cleared before employment',
      'Initial training and competency evaluation required before client contact',
    ],
  },

  {
    id: 'CO-B3',
    itemId: 'B3',
    stateCode: 'CO',
    title: 'DORA Professional License Verification',
    description: 'Colorado requires DORA verification for licensed professional staff before employment',
    severity: 'high',
    regulationCode: '6 CCR 1011-1',
    regulationName: 'License Verification',
    agencyName: 'Department of Regulatory Agencies',
    agencyAbbr: 'DORA',
    agencyPhone: '(303) 894-7855',
    agencyUrl: 'https://dora.colorado.gov/',
    questions: [
      {
        id: 'q1',
        question: 'Do you verify DORA licenses for all licensed professional staff (RNs, LPNs, CNAs) BEFORE employing them?',
        inputType: 'select',
        options: [
          { value: 'always', label: 'Yes, always verified before employment with copy in file' },
          { value: 'hire_only', label: 'Only at time of hire, no ongoing verification' },
          { value: 'no', label: 'No formal verification process' },
        ],
        required: true,
        helpText: 'Part 5.7(E) requires DORA contact before employing any individual to provide direct care',
      },
      {
        id: 'q2',
        question: 'Do you maintain a copy of the DORA inquiry in the personnel file?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        helpText: 'Part 5.7(E) requires copy of inquiry in personnel file',
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'always' },
        { condition: 'equals', questionId: 'q2', value: 'yes' },
      ], result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q1', value: 'hire_only', result: 'AT_RISK' },
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
      { condition: 'equals', questionId: 'q2', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'DORA license verification process meets 6 CCR 1011-1 Chapter 26, Part 5.7(E) requirements.',
      atRisk: 'DORA verification should be ongoing, not just at hire. Implement periodic verification.',
      nonCompliant: 'Failure to verify DORA licenses before employment violates Part 5.7(E). Verify all licenses at dora.colorado.gov.',
      unknown: 'Verify all professional licenses with DORA at (303) 894-7855.',
    },
    stateNotes: [
      'DORA verification required BEFORE employing any individual to provide direct consumer care',
      'Must verify license, registration, or certification exists and is in good standing',
      'Copy of inquiry must be placed in personnel file',
      'Applies to RNs, LPNs, CNAs, therapists, and other licensed professionals',
    ],
  },

  {
    id: 'CO-B4',
    itemId: 'B4',
    stateCode: 'CO',
    title: 'Employee Health Documentation',
    description: 'Health screening documentation for all direct care workers',
    severity: 'high',
    regulationCode: '6 CCR 1011-1',
    regulationName: 'Personnel Health',
    agencyName: 'Colorado Department of Public Health and Environment',
    agencyAbbr: 'CDPHE',
    agencyPhone: '(303) 692-2800',
    agencyUrl: 'https://cdphe.colorado.gov/home-care-agencies',
    questions: [
      {
        id: 'q1',
        question: 'Which health documentation do you maintain for all workers? (Select all that apply)',
        inputType: 'checklist',
        options: [
          { value: 'tb', label: 'TB screening results' },
          { value: 'physical', label: 'Pre-employment physical evaluation' },
          { value: 'communicable', label: 'Communicable disease screening' },
          { value: 'fitness', label: 'Fitness for duty documentation' },
        ],
        required: true,
        helpText: 'Part 5.6(C) requires written policies on pre-employment physical evaluations and personnel health',
      },
    ],
    scoringRules: [
      { condition: 'countAtLeast', questionId: 'q1', value: 3, result: 'COMPLIANT' },
      { condition: 'countAtLeast', questionId: 'q1', value: 2, result: 'AT_RISK' },
      { condition: 'countLessThan', questionId: 'q1', value: 2, result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Employee health documentation meets Colorado requirements per Part 5.6(C).',
      atRisk: 'Some health documentation may be incomplete. Review all worker files for required health records.',
      nonCompliant: 'Health documentation is insufficient. Colorado requires TB screening at minimum for all direct care workers.',
      unknown: 'Review health documentation requirements.',
    },
    stateNotes: [
      'Part 5.6(C) requires written policies on pre-employment physical evaluations',
      'Personnel health and communicable disease policies required',
      'TB screening documentation must be maintained in personnel file',
      'Workers must be cleared before providing direct care',
    ],
  },

  // ============================================
  // SECTION C: BACKGROUND SCREENING
  // ============================================
  {
    id: 'CO-C1',
    itemId: 'C1',
    stateCode: 'CO',
    title: 'CBI Background Check Requirements',
    description: 'Colorado requires CBI background check within 90 days prior to employment',
    severity: 'high',
    regulationCode: '6 CCR 1011-1',
    regulationName: 'Background Check',
    agencyName: 'Colorado Bureau of Investigation',
    agencyAbbr: 'CBI',
    agencyPhone: '(303) 239-4208',
    agencyUrl: 'https://cbi.colorado.gov/',
    questions: [
      {
        id: 'q1',
        question: 'For ALL currently employed home care workers, do you have a CBI background check on file?',
        inputType: 'select',
        options: [
          { value: 'all_90day', label: 'Yes, all have CBI check conducted within 90 days prior to employment' },
          { value: 'all_later', label: 'Yes, but some checks were done after employment started' },
          { value: 'most', label: 'Most have CBI checks, some are missing' },
          { value: 'no', label: 'No / Not tracking this' },
        ],
        required: true,
        helpText: 'Part 5.7(B) requires CBI check within 90 days PRIOR to employment',
      },
      {
        id: 'q2',
        question: 'Do any workers begin providing services BEFORE their background check is completed?',
        inputType: 'binary',
        options: [
          { value: 'no', label: 'No - never before clearance' },
          { value: 'yes', label: 'Yes - sometimes they start early' },
        ],
        required: true,
      },
    ],
    scoringRules: [
      // NON_COMPLIANT if workers start before clearance (per Part 4.3(A)(2) - no provisional license allowed)
      { condition: 'equals', questionId: 'q2', value: 'yes', result: 'NON_COMPLIANT' },
      // NON_COMPLIANT if missing checks entirely
      { condition: 'anyTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'most' },
        { condition: 'equals', questionId: 'q1', value: 'no' },
      ], result: 'NON_COMPLIANT' },
      // COMPLIANT only if all checks within 90 days AND never start before clearance
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'all_90day' },
        { condition: 'equals', questionId: 'q2', value: 'no' },
      ], result: 'COMPLIANT' },
      // AT_RISK if checks done but timing is off
      { condition: 'equals', questionId: 'q1', value: 'all_later', result: 'AT_RISK' },
    ],
    reportLanguage: {
      compliant: 'CBI background check documentation is complete for all workers per Part 5.7(B). Checks were conducted within 90 days prior to employment.',
      atRisk: 'Background checks may not meet the 90-day prior to employment requirement. Review timing compliance per Part 5.7(B).',
      nonCompliant: 'VIOLATION: Workers are starting before CBI clearance OR required checks are missing. Per Part 4.3(A)(2), no provisional license allowed. Immediately remove uncleared workers.',
      unknown: 'Audit all worker files for CBI background check documentation per Part 5.7(B).',
    },
    stateNotes: [
      'CBI check must be conducted within 90 days PRIOR to employment',
      'If check is older than 90 days at time of employment, a new check is required',
      'Cost may be paid by agency or individual seeking employment per Part 5.7(C)',
      'No provisional license granted before completion of criminal background check per Part 4.3(A)(2)',
    ],
  },

  {
    id: 'CO-C2',
    itemId: 'C2',
    stateCode: 'CO',
    title: 'CAPS Check (Colorado Adult Protective Services Data System)',
    description: 'Colorado requires CAPS Check before employing direct care workers',
    severity: 'high',
    regulationCode: '6 CCR 1011-1',
    regulationName: 'CAPS Check',
    agencyName: 'Colorado Department of Human Services',
    agencyAbbr: 'CDHS',
    agencyPhone: '(844) 264-5437',
    agencyUrl: 'https://cdhs.colorado.gov/aps',
    questions: [
      {
        id: 'q1',
        question: 'Do you verify all potential employees against the Colorado Adult Protective Services Data System (CAPS Check) BEFORE hiring?',
        inputType: 'select',
        options: [
          { value: 'always', label: 'Yes, always before hire per C.R.S. 26-3.1-111' },
          { value: 'sometimes', label: 'Sometimes, but not consistently' },
          { value: 'no', label: 'No' },
        ],
        required: true,
        helpText: 'Part 5.7(D) requires compliance with CAPS Check per C.R.S. 26-3.1-111 and Chapter 2, Part 2.3.6',
      },
      {
        id: 'q2',
        question: 'Do you document CAPS Check results in personnel files?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'always' },
        { condition: 'equals', questionId: 'q2', value: 'yes' },
      ], result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q1', value: 'sometimes', result: 'AT_RISK' },
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'CAPS Check verification is completed for all employees before hire per Part 5.7(D) and C.R.S. 26-3.1-111.',
      atRisk: 'CAPS Check verification is not being completed consistently. Implement a process to verify all potential hires.',
      nonCompliant: 'Failure to complete CAPS Check before hire violates Part 5.7(D) and C.R.S. 26-3.1-111. Contact CDHS APS at (844) 264-5437.',
      unknown: 'Verify CAPS Check process is in place.',
    },
    stateNotes: [
      'CAPS Check required BEFORE employing any individual to provide direct consumer care',
      'Must comply with C.R.S. 26-3.1-111 and 6 CCR 1011-1 Chapter 2, Part 2.3.6',
      'Checks for substantiated findings of abuse/neglect/exploitation',
      'Different from CBI criminal background check - both are required',
    ],
  },

  {
    id: 'CO-C3',
    itemId: 'C3',
    stateCode: 'CO',
    title: 'OIG Exclusion List Verification',
    description: 'Federal requirement to check OIG exclusion list for Medicaid/Medicare providers',
    severity: 'high',
    regulationCode: '42 CFR 1001.1901',
    regulationName: 'OIG Exclusion',
    agencyName: 'Office of Inspector General',
    agencyAbbr: 'OIG',
    agencyPhone: '(800) 447-8477',
    agencyUrl: 'https://exclusions.oig.hhs.gov/',
    questions: [
      {
        id: 'q1',
        question: 'Do you check the OIG exclusion list for all employees?',
        inputType: 'select',
        options: [
          { value: 'monthly', label: 'Yes, at hire and monthly thereafter' },
          { value: 'hire_only', label: 'Only at time of hire' },
          { value: 'no', label: 'No' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'Do you provide Medicaid (Health First Colorado) or Medicare services?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No - private pay only' },
        ],
      },
    ],
    scoringRules: [
      { condition: 'equals', questionId: 'q1', value: 'monthly', result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q1', value: 'hire_only', result: 'AT_RISK' },
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'no' },
        { condition: 'equals', questionId: 'q2', value: 'yes' },
      ], result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'OIG exclusion list verification meets federal requirements with monthly screening.',
      atRisk: 'OIG checks should be performed monthly, not just at hire. Implement monthly screening.',
      nonCompliant: 'Employing excluded individuals while billing Medicaid/Medicare is federal fraud. Check exclusions.oig.hhs.gov immediately.',
      unknown: 'Verify OIG exclusion checking process.',
    },
    stateNotes: [
      'OIG exclusion check required for Medicaid/Medicare providers per 42 CFR 1001.1901',
      'Monthly screening recommended per OIG guidance',
      'Employing excluded individuals results in federal sanctions',
      'Check at exclusions.oig.hhs.gov',
    ],
  },

  {
    id: 'CO-C4',
    itemId: 'C4',
    stateCode: 'CO',
    title: 'SAM Exclusion Verification',
    description: 'Federal requirement to check SAM.gov exclusion list',
    severity: 'high',
    regulationCode: 'FAR 52.209-6',
    regulationName: 'SAM Exclusions',
    agencyName: 'General Services Administration',
    agencyAbbr: 'GSA',
    agencyPhone: '(866) 606-8220',
    agencyUrl: 'https://sam.gov/',
    questions: [
      {
        id: 'q1',
        question: 'Do you check SAM.gov exclusion list for all employees and contractors?',
        inputType: 'select',
        options: [
          { value: 'regular', label: 'Yes, at hire and regularly thereafter' },
          { value: 'hire_only', label: 'Only at time of hire' },
          { value: 'no', label: 'No' },
        ],
        required: true,
      },
    ],
    scoringRules: [
      { condition: 'equals', questionId: 'q1', value: 'regular', result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q1', value: 'hire_only', result: 'AT_RISK' },
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'SAM.gov exclusion verification process is in place.',
      atRisk: 'SAM checks should be performed regularly, not just at hire.',
      nonCompliant: 'SAM exclusion verification is required for federal program participation. Check sam.gov immediately.',
      unknown: 'Verify SAM exclusion checking process.',
    },
    stateNotes: [
      'SAM.gov exclusion check required per FAR 52.209-6',
      'Applies to employees and contractors',
      'Regular screening recommended, not just at hire',
      'Check at sam.gov',
    ],
  },

  // ============================================
  // SECTION D: HEALTH & SAFETY
  // ============================================
  {
    id: 'CO-D1',
    itemId: 'D1',
    stateCode: 'CO',
    title: 'TB Screening Requirements',
    description: 'Colorado requires TB screening for all direct care workers per personnel health policies',
    severity: 'high',
    regulationCode: '6 CCR 1011-1',
    regulationName: 'Communicable Disease',
    agencyName: 'Colorado Department of Public Health and Environment',
    agencyAbbr: 'CDPHE',
    agencyPhone: '(303) 692-2800',
    agencyUrl: 'https://cdphe.colorado.gov/home-care-agencies',
    questions: [
      {
        id: 'q1',
        question: 'Do ALL your home care workers have current TB screening on file?',
        inputType: 'select',
        options: [
          { value: 'all', label: 'Yes, all workers have current TB screening' },
          { value: 'most', label: 'Most do, some may be expired' },
          { value: 'no', label: 'No / Not tracking this' },
        ],
        required: true,
        helpText: 'Part 5.6(C) requires personnel health policies including communicable disease prevention',
      },
      {
        id: 'q2',
        question: 'How do you track TB screening expiration dates?',
        inputType: 'select',
        options: [
          { value: 'system', label: 'Automated system with reminders' },
          { value: 'manual', label: 'Manual tracking (spreadsheet, calendar)' },
          { value: 'none', label: 'No active tracking system' },
        ],
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'all' },
        { condition: 'includes', questionId: 'q2', value: ['system', 'manual'] },
      ], result: 'COMPLIANT' },
      { condition: 'anyTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'most' },
        { condition: 'equals', questionId: 'q2', value: 'none' },
      ], result: 'AT_RISK' },
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'TB screening documentation is current for all workers per Colorado requirements.',
      atRisk: 'Some TB screenings may be expired. Review all worker files and schedule testing for anyone with expired clearance.',
      nonCompliant: 'Workers are providing services without valid TB screening. This creates health risks. Immediately obtain TB testing for all workers without current clearance.',
      unknown: 'Verify TB screening status for all workers.',
    },
    stateNotes: [
      'Part 5.6(C) requires personnel health policies including communicable disease prevention',
      'TB screening required for all direct care workers before client contact',
      'Track expiration dates to ensure continuous compliance',
      'Workers with expired TB clearance must be removed from direct care',
    ],
  },

  {
    id: 'CO-D2',
    itemId: 'D2',
    stateCode: 'CO',
    title: 'Infection Prevention and Control Program',
    description: 'Colorado requires infection prevention and control policies and training',
    severity: 'high',
    regulationCode: '6 CCR 1011-1',
    regulationName: 'Infection Control',
    agencyName: 'Colorado Department of Public Health and Environment',
    agencyAbbr: 'CDPHE',
    agencyPhone: '(303) 692-2800',
    agencyUrl: 'https://cdphe.colorado.gov/home-care-agencies',
    questions: [
      {
        id: 'q1',
        question: 'Does your agency have written infection prevention and control policies and procedures?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        required: true,
        helpText: 'Part 5.6(A) requires training on infection control at hire and at least annually',
      },
      {
        id: 'q2',
        question: 'Do all workers receive infection control training?',
        inputType: 'select',
        options: [
          { value: 'initial_annual', label: 'Yes, at hire and at least annually per Part 5.6(A)' },
          { value: 'initial_only', label: 'Yes, at hire only' },
          { value: 'no', label: 'No formal training' },
        ],
      },
      {
        id: 'q3',
        question: 'Do you evaluate infection control policies at least annually and document changes?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes, per Part 5.6(B)' },
          { value: 'no', label: 'No' },
        ],
        helpText: 'Part 5.6(B) requires annual evaluation and documented changes',
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'yes' },
        { condition: 'equals', questionId: 'q2', value: 'initial_annual' },
        { condition: 'equals', questionId: 'q3', value: 'yes' },
      ], result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q2', value: 'initial_only', result: 'AT_RISK' },
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
      { condition: 'equals', questionId: 'q2', value: 'no', result: 'NON_COMPLIANT' },
      { condition: 'equals', questionId: 'q3', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Infection prevention and control program meets 6 CCR 1011-1 Chapter 26, Part 5.6 requirements.',
      atRisk: 'Infection control training should be conducted at hire AND at least annually per Part 5.6(A). Policies must be evaluated annually per Part 5.6(B).',
      nonCompliant: 'Written infection prevention and control program is required per Part 5.6. Develop program and implement training immediately.',
      unknown: 'Review infection control program against Part 5.6 requirements.',
    },
    stateNotes: [
      'Part 5.6(A): Training required at hire AND at least annually',
      'Part 5.6(B): Evaluate policies at least annually, document substantive changes',
      'Part 5.6(C): Written policies on pre-employment physical evaluations and personnel health',
      'Part 5.6(D): Occurrence reporting per Chapter 2, Part 4.2',
    ],
  },

  {
    id: 'CO-D3',
    itemId: 'D3',
    stateCode: 'CO',
    title: 'Bloodborne Pathogen Compliance',
    description: 'OSHA bloodborne pathogen standards apply to home care',
    severity: 'high',
    regulationCode: '29 CFR 1910.1030',
    regulationName: 'Bloodborne Pathogens',
    agencyName: 'Occupational Safety and Health Administration',
    agencyAbbr: 'OSHA',
    agencyPhone: '(800) 321-6742',
    agencyUrl: 'https://www.osha.gov/',
    questions: [
      {
        id: 'q1',
        question: 'Do you have a written Exposure Control Plan?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'Do all workers receive annual bloodborne pathogen training?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'yes' },
        { condition: 'equals', questionId: 'q2', value: 'yes' },
      ], result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q1', value: 'yes', result: 'AT_RISK' },
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Bloodborne pathogen program meets OSHA requirements.',
      atRisk: 'Annual training may not be current for all workers. Verify training records.',
      nonCompliant: 'Written Exposure Control Plan is required by OSHA. Develop plan and train workers immediately.',
      unknown: 'Review OSHA bloodborne pathogen requirements.',
    },
    stateNotes: [
      'OSHA 29 CFR 1910.1030 applies to home care workers',
      'Written Exposure Control Plan required',
      'Annual bloodborne pathogen training required for all workers',
      'Hepatitis B vaccination must be offered to at-risk employees',
    ],
  },

  // ============================================
  // SECTION E: TRAINING & COMPETENCY
  // ============================================
  {
    id: 'CO-E1',
    itemId: 'E1',
    stateCode: 'CO',
    title: 'Personal Care Worker Initial Training',
    description: 'Colorado requires initial training before client contact per Part 7.3',
    severity: 'high',
    regulationCode: '6 CCR 1011-1',
    regulationName: 'Worker Training',
    agencyName: 'Colorado Department of Public Health and Environment',
    agencyAbbr: 'CDPHE',
    agencyPhone: '(303) 692-2800',
    agencyUrl: 'https://cdphe.colorado.gov/home-care-agencies',
    questions: [
      {
        id: 'q1',
        question: 'Do ALL new personal care workers complete initial training BEFORE any client contact? (Select all training topics covered)',
        inputType: 'checklist',
        options: [
          { value: 'duties', label: 'Personnel duties and responsibilities including incident/mandatory reporting' },
          { value: 'rules', label: 'Rules for non-medical care per Chapter 26' },
          { value: 'differences', label: 'Differences in homemaker and personal care' },
          { value: 'rights', label: 'Consumer rights, freedom from abuse, confidentiality' },
          { value: 'safety', label: 'Basic health and safety (home safety, fall prevention, hand washing, infection control)' },
          { value: 'populations', label: 'Needs of populations served' },
          { value: 'homemaking', label: 'Homemaking and housekeeping skills evaluation' },
        ],
        required: true,
        helpText: 'Part 7.3 specifies required initial training topics',
      },
    ],
    scoringRules: [
      { condition: 'countAtLeast', questionId: 'q1', value: 7, result: 'COMPLIANT' },
      { condition: 'countAtLeast', questionId: 'q1', value: 5, result: 'AT_RISK' },
      { condition: 'countLessThan', questionId: 'q1', value: 5, result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Initial training program meets 6 CCR 1011-1 Chapter 26, Part 7.3 requirements.',
      atRisk: 'Initial training may be incomplete. Colorado requires comprehensive training BEFORE first client contact per Part 7.3.',
      nonCompliant: 'Workers are providing services without required initial training. This violates Part 7.3. Immediately complete training for any worker who started without it.',
      unknown: 'Review initial training program against Part 7.3 requirements.',
    },
    stateNotes: [
      'Initial training must include all topics in Part 7.3(B)',
      'Training and core competency evaluation of homemaking/housekeeping skills required before completion of initial training',
      'Must evaluate maintenance of clean, safe, and healthy environment',
      'Must evaluate appropriate use of cleaning products and equipment',
    ],
  },

  {
    id: 'CO-E2',
    itemId: 'E2',
    stateCode: 'CO',
    title: 'Annual Training Requirements',
    description: 'Colorado requires annual training per infection control and agency policies',
    severity: 'high',
    regulationCode: '6 CCR 1011-1',
    regulationName: 'Annual Training',
    agencyName: 'Colorado Department of Public Health and Environment',
    agencyAbbr: 'CDPHE',
    agencyPhone: '(303) 692-2800',
    agencyUrl: 'https://cdphe.colorado.gov/home-care-agencies',
    questions: [
      {
        id: 'q1',
        question: 'Do ALL home care workers complete annual training?',
        inputType: 'select',
        options: [
          { value: 'all', label: 'Yes, all workers complete required annual training' },
          { value: 'most', label: 'Most complete training, some are behind' },
          { value: 'no', label: 'No structured annual training program' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'Which topics does your annual training cover? (Select all that apply)',
        inputType: 'checklist',
        options: [
          { value: 'infection', label: 'Infection prevention and control (required per Part 5.6(A))' },
          { value: 'rights', label: 'Consumer rights' },
          { value: 'safety', label: 'Safety procedures' },
          { value: 'abuse', label: 'Abuse recognition and mandatory reporting' },
          { value: 'emergency', label: 'Emergency procedures' },
          { value: 'population', label: 'Population-specific training' },
        ],
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'all' },
        { condition: 'countAtLeast', questionId: 'q2', value: 5 },
      ], result: 'COMPLIANT' },
      { condition: 'anyTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'most' },
        { condition: 'countAtLeast', questionId: 'q2', value: 3 },
      ], result: 'AT_RISK' },
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Annual training program meets Colorado requirements including Part 5.6(A) infection control training.',
      atRisk: 'Some workers may be behind on annual training. Review training records and schedule catch-up training.',
      nonCompliant: 'No structured annual training program exists. Part 5.6(A) requires infection control training at least annually. Implement annual training program immediately.',
      unknown: 'Review annual training program requirements.',
    },
    stateNotes: [
      'Part 5.6(A) requires infection control training at hire AND at least annually',
      'Annual training should cover rights, safety, abuse recognition, emergency procedures',
      'Document all training in personnel files with dates, topics, and signatures',
      'Track training due dates to ensure timely completion',
    ],
  },

  {
    id: 'CO-E3',
    itemId: 'E3',
    stateCode: 'CO',
    title: 'Abuse Recognition and Mandatory Reporting Training',
    description: 'Colorado requires training on abuse recognition and mandatory reporting per C.R.S. 26-3.1-102',
    severity: 'high',
    regulationCode: 'C.R.S. 26-3.1-102',
    regulationName: 'Mandatory Reporting',
    agencyName: 'Colorado Department of Human Services',
    agencyAbbr: 'CDHS',
    agencyPhone: '(844) 264-5437',
    agencyUrl: 'https://cdhs.colorado.gov/aps',
    questions: [
      {
        id: 'q1',
        question: 'Do ALL your home care workers receive training on abuse recognition and mandatory reporting?',
        inputType: 'select',
        options: [
          { value: 'all', label: 'Yes, all workers are trained (included in initial training per Part 7.3)' },
          { value: 'most', label: 'Most workers are trained' },
          { value: 'no', label: 'No formal training' },
        ],
        required: true,
        helpText: 'Part 7.3(B)(1) requires training on duties including incident reporting and mandatory reporting',
      },
      {
        id: 'q2',
        question: 'Do workers know how to report suspected abuse to Adult Protective Services?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes, reporting procedures are clear' },
          { value: 'no', label: 'No / Not sure' },
        ],
        required: true,
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'all' },
        { condition: 'equals', questionId: 'q2', value: 'yes' },
      ], result: 'COMPLIANT' },
      { condition: 'anyTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'most' },
        { condition: 'equals', questionId: 'q2', value: 'no' },
      ], result: 'AT_RISK' },
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'All workers are trained on abuse recognition and mandatory reporting per C.R.S. 26-3.1-102.',
      atRisk: 'Some workers may lack proper abuse reporting training. Colorado law requires all caregivers to be trained as mandatory reporters.',
      nonCompliant: 'Failure to train workers on mandatory reporting requirements violates Colorado law. Report abuse to APS at (844) 264-5437.',
      unknown: 'Verify all workers have received abuse recognition and reporting training.',
    },
    stateNotes: [
      'Home care workers are mandatory reporters under C.R.S. 26-3.1-102',
      'Must report known or suspected abuse, neglect, or exploitation of at-risk adults',
      'Training on mandatory reporting is required in initial training per Part 7.3(B)(1)',
      'Report to Colorado APS: (844) 264-5437',
    ],
  },

  {
    id: 'CO-E4',
    itemId: 'E4',
    stateCode: 'CO',
    title: 'Competency Evaluation Process',
    description: 'Colorado requires competency evaluation before client contact',
    severity: 'high',
    regulationCode: '6 CCR 1011-1',
    regulationName: 'Competency Evaluation',
    agencyName: 'Colorado Department of Public Health and Environment',
    agencyAbbr: 'CDPHE',
    agencyPhone: '(303) 692-2800',
    agencyUrl: 'https://cdphe.colorado.gov/home-care-agencies',
    questions: [
      {
        id: 'q1',
        question: 'Do you conduct competency evaluations for all new personal care workers before completion of initial training?',
        inputType: 'select',
        options: [
          { value: 'before_client', label: 'Yes, before first client contact' },
          { value: 'after_start', label: 'Yes, but after they begin work' },
          { value: 'no', label: 'No formal competency evaluation' },
        ],
        required: true,
        helpText: 'Part 7.3(B)(2)(i) requires core competency evaluation of homemaking/housekeeping skills before completion of initial training',
      },
      {
        id: 'q2',
        question: 'Which competencies do you evaluate? (Select all that apply)',
        inputType: 'checklist',
        options: [
          { value: 'clean_environment', label: 'Maintenance of clean, safe, and healthy environment' },
          { value: 'cleaning_products', label: 'Appropriate use of cleaning products and equipment' },
          { value: 'adl', label: 'Activities of daily living assistance' },
          { value: 'safety', label: 'Safety and emergency response' },
          { value: 'infection', label: 'Infection control practices' },
        ],
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'before_client' },
        { condition: 'countAtLeast', questionId: 'q2', value: 4 },
      ], result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q1', value: 'after_start', result: 'AT_RISK' },
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Competency evaluation process meets 6 CCR 1011-1 Chapter 26, Part 7.3 requirements.',
      atRisk: 'Competency evaluations should be completed before first client contact per Part 7.3.',
      nonCompliant: 'No competency evaluation process exists. This violates Part 7.3. Implement immediately.',
      unknown: 'Review competency evaluation requirements.',
    },
    stateNotes: [
      'Part 7.3(B)(2)(i) requires core competency evaluation before completion of initial training',
      'Must evaluate maintenance of clean, safe, and healthy environment',
      'Must evaluate appropriate use of cleaning products and equipment',
      'Document competency results in personnel file',
    ],
  },

  {
    id: 'CO-E5',
    itemId: 'E5',
    stateCode: 'CO',
    title: 'Training Documentation and Records',
    description: 'Proper documentation of all training activities',
    severity: 'high',
    regulationCode: '6 CCR 1011-1',
    regulationName: 'Training Documentation',
    agencyName: 'Colorado Department of Public Health and Environment',
    agencyAbbr: 'CDPHE',
    agencyPhone: '(303) 692-2800',
    agencyUrl: 'https://cdphe.colorado.gov/home-care-agencies',
    questions: [
      {
        id: 'q1',
        question: 'Do you maintain documentation for all training completed by workers in their personnel files?',
        inputType: 'select',
        options: [
          { value: 'comprehensive', label: 'Yes, comprehensive records for all training' },
          { value: 'partial', label: 'Some training is documented' },
          { value: 'no', label: 'No systematic documentation' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'What information is included in training records? (Select all that apply)',
        inputType: 'checklist',
        options: [
          { value: 'date', label: 'Date of training' },
          { value: 'topic', label: 'Training topic/content' },
          { value: 'hours', label: 'Hours/duration' },
          { value: 'trainer', label: 'Trainer name/credentials' },
          { value: 'signature', label: 'Worker signature/acknowledgment' },
        ],
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'comprehensive' },
        { condition: 'countAtLeast', questionId: 'q2', value: 4 },
      ], result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q1', value: 'partial', result: 'AT_RISK' },
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Training documentation meets Colorado requirements.',
      atRisk: 'Training documentation is incomplete. Ensure all required elements are captured.',
      nonCompliant: 'No systematic training documentation. CDPHE surveyors will cite this deficiency.',
      unknown: 'Review training documentation requirements.',
    },
    stateNotes: [
      'Training records must be maintained in personnel files',
      'Document date, topic/content, hours, trainer name, and worker signature',
      'CDPHE surveyors review training documentation during surveys',
      'Incomplete documentation is a common survey deficiency',
    ],
  },

  // ============================================
  // SECTION F: CARE DELIVERY
  // ============================================
  {
    id: 'CO-F1',
    itemId: 'F1',
    stateCode: 'CO',
    title: 'Personal Care Service Scope Limitations',
    description: 'Colorado defines specific scope limitations for personal care workers',
    severity: 'high',
    regulationCode: '6 CCR 1011-1',
    regulationName: 'Service Scope',
    agencyName: 'Colorado Department of Public Health and Environment',
    agencyAbbr: 'CDPHE',
    agencyPhone: '(303) 692-2800',
    agencyUrl: 'https://cdphe.colorado.gov/home-care-agencies',
    questions: [
      {
        id: 'q1',
        question: 'Do your personal care workers understand and follow Colorado scope limitations? (Select all that are understood)',
        inputType: 'checklist',
        options: [
          { value: 'no_nail_trim', label: 'No nail trimming (filing only per Part 7.4(B)(6)(b))' },
          { value: 'no_skilled_mouth', label: 'No mouth care for unconscious or aspiration-risk consumers' },
          { value: 'medicated_shampoo', label: 'Over-the-counter medicated shampoo only with training and annual competency evaluation' },
          { value: 'no_medication_admin', label: 'No medication administration without QMAP certification' },
          { value: 'scope_doc', label: 'Written scope of services documentation' },
        ],
        required: true,
        helpText: 'Part 7.4 and 7.5 specify what personal care workers may and may not do',
      },
    ],
    scoringRules: [
      { condition: 'countAtLeast', questionId: 'q1', value: 5, result: 'COMPLIANT' },
      { condition: 'countAtLeast', questionId: 'q1', value: 3, result: 'AT_RISK' },
      { condition: 'countLessThan', questionId: 'q1', value: 3, result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Personal care workers understand and follow Colorado scope limitations per Part 7.4-7.5.',
      atRisk: 'Some scope limitations may not be understood. Review Part 7.4-7.5 requirements with all workers.',
      nonCompliant: 'Workers may be performing tasks outside their scope. This creates liability and violates Colorado regulations.',
      unknown: 'Review personal care scope limitations.',
    },
    stateNotes: [
      'Part 7.4(B)(6)(b): Personal care workers may file nails but NOT trim them',
      'Part 7.4(B)(7)(b): Mouth care for unconscious or aspiration-risk consumers requires skilled agency',
      'Part 7.4(B)(4)(c): Over-the-counter medicated shampoo requires training and annual competency evaluation',
      'QMAP certification required for medication administration (SLS/CES waiver only)',
    ],
  },

  {
    id: 'CO-F2',
    itemId: 'F2',
    stateCode: 'CO',
    title: 'Consumer Care Documentation',
    description: 'Documentation of services provided to consumers',
    severity: 'high',
    regulationCode: '6 CCR 1011-1',
    regulationName: 'Service Documentation',
    agencyName: 'Colorado Department of Public Health and Environment',
    agencyAbbr: 'CDPHE',
    agencyPhone: '(303) 692-2800',
    agencyUrl: 'https://cdphe.colorado.gov/home-care-agencies',
    questions: [
      {
        id: 'q1',
        question: 'Do workers document services provided at each visit?',
        inputType: 'select',
        options: [
          { value: 'always', label: 'Yes, always at each visit' },
          { value: 'usually', label: 'Usually, but not always' },
          { value: 'no', label: 'No visit documentation' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'What information is captured in visit documentation? (Select all that apply)',
        inputType: 'checklist',
        options: [
          { value: 'date_time', label: 'Date and time' },
          { value: 'services', label: 'Services provided' },
          { value: 'observations', label: 'Consumer observations' },
          { value: 'signature', label: 'Worker signature' },
          { value: 'consumer_sig', label: 'Consumer/representative verification' },
        ],
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'always' },
        { condition: 'countAtLeast', questionId: 'q2', value: 4 },
      ], result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q1', value: 'usually', result: 'AT_RISK' },
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Service delivery documentation meets Colorado requirements.',
      atRisk: 'Visit documentation should be completed for every visit. Ensure consistency.',
      nonCompliant: 'No visit documentation. This creates compliance and liability risks.',
      unknown: 'Review service documentation requirements.',
    },
    stateNotes: [
      'Document services provided at each visit',
      'Include date, time, services, observations, and signatures',
      'Consumer/representative verification recommended',
      'Documentation supports billing and demonstrates care quality',
    ],
  },

  // ============================================
  // SECTION G: DOCUMENTATION/ADMIN
  // ============================================
  {
    id: 'CO-G1',
    itemId: 'G1',
    stateCode: 'CO',
    title: 'Record Retention Requirements',
    description: 'Colorado requires minimum record retention periods',
    severity: 'medium',
    regulationCode: '6 CCR 1011-1',
    regulationName: 'Record Retention',
    agencyName: 'Colorado Department of Public Health and Environment',
    agencyAbbr: 'CDPHE',
    agencyPhone: '(303) 692-2800',
    agencyUrl: 'https://cdphe.colorado.gov/home-care-agencies',
    questions: [
      {
        id: 'q1',
        question: 'How long do you retain complaint records?',
        inputType: 'select',
        options: [
          { value: '2plus', label: '2 years or longer (meets CO requirement per Part 5.4(5)(a))' },
          { value: '1year', label: '1 year' },
          { value: 'less1', label: 'Less than 1 year' },
          { value: 'unsure', label: 'Not sure / no formal policy' },
        ],
        required: true,
        helpText: 'Part 5.4(5)(a) requires complaint records maintained for at least 2 years',
      },
      {
        id: 'q2',
        question: 'How long do you retain consumer and personnel records?',
        inputType: 'select',
        options: [
          { value: '7plus', label: '7 years or longer' },
          { value: '5to6', label: '5-6 years' },
          { value: 'less5', label: 'Less than 5 years' },
          { value: 'unsure', label: 'Not sure / no formal policy' },
        ],
        helpText: 'Best practice is 7 years for medical records; check Medicaid requirements if applicable',
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: '2plus' },
        { condition: 'includes', questionId: 'q2', value: ['7plus', '5to6'] },
      ], result: 'COMPLIANT' },
      { condition: 'anyTrue', value: [
        { condition: 'equals', questionId: 'q1', value: '1year' },
        { condition: 'equals', questionId: 'q2', value: 'less5' },
      ], result: 'AT_RISK' },
      { condition: 'includes', questionId: 'q1', value: ['less1', 'unsure'], result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Your record retention policy meets Colorado requirements.',
      atRisk: 'Record retention may not meet all requirements. Complaint records require 2 years minimum per Part 5.4(5)(a).',
      nonCompliant: 'Your record retention does not meet Colorado requirements. Update your retention policy immediately.',
      unknown: 'Review record retention requirements.',
    },
    stateNotes: [
      'Part 5.4(5)(a) requires complaint records maintained for at least 2 years',
      'Best practice is 7 years for medical records',
      'Medicaid providers may have additional retention requirements',
      'Records must be available for audit and inspection',
    ],
  },

  {
    id: 'CO-G2',
    itemId: 'G2',
    stateCode: 'CO',
    title: 'Electronic Visit Verification (EVV)',
    description: 'EVV required for Medicaid-funded personal care services',
    severity: 'high',
    regulationCode: '21st Century Cures Act',
    regulationName: 'EVV',
    agencyName: 'Colorado Department of Health Care Policy and Financing',
    agencyAbbr: 'HCPF',
    agencyPhone: '(303) 866-2993',
    agencyUrl: 'https://hcpf.colorado.gov/',
    questions: [
      {
        id: 'q1',
        question: 'Do you provide Medicaid (Health First Colorado) funded personal care services?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No - private pay only' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'Do you use a Colorado-approved EVV system to document visits?',
        inputType: 'select',
        options: [
          { value: 'yes_compliant', label: 'Yes, fully implemented and compliant' },
          { value: 'yes_partial', label: 'Yes, but not for all visits' },
          { value: 'no', label: 'No EVV system in place' },
        ],
        showCondition: {
          questionId: 'q1',
          operator: 'equals',
          value: 'yes',
        },
      },
    ],
    scoringRules: [
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q2', value: 'yes_compliant', result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q2', value: 'yes_partial', result: 'AT_RISK' },
      { condition: 'equals', questionId: 'q2', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'EVV system is properly implemented for Health First Colorado funded services.',
      atRisk: 'EVV is not being used consistently for all Medicaid visits. Federal law requires EVV for all personal care services. Ensure 100% EVV compliance.',
      nonCompliant: 'Colorado Medicaid personal care services require EVV per federal law. Failure to implement EVV can result in claim denials and sanctions. Contact HCPF at (303) 866-2993.',
      unknown: 'If you provide Medicaid services, verify EVV compliance.',
    },
    stateNotes: [
      'EVV required per 21st Century Cures Act for Medicaid personal care services',
      'Must use Colorado-approved EVV system',
      'EVV captures location, date, time, service type, and provider identity',
      'Non-compliance can result in claim denials and sanctions from HCPF',
    ],
  },

  {
    id: 'CO-G3',
    itemId: 'G3',
    stateCode: 'CO',
    title: 'HIPAA Privacy Compliance',
    description: 'Health Insurance Portability and Accountability Act compliance',
    severity: 'high',
    regulationCode: '45 CFR 164',
    regulationName: 'HIPAA',
    agencyName: 'U.S. Department of Health and Human Services',
    agencyAbbr: 'HHS',
    agencyPhone: '(800) 368-1019',
    agencyUrl: 'https://www.hhs.gov/hipaa/',
    questions: [
      {
        id: 'q1',
        question: 'Do you have written HIPAA privacy policies and procedures?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'Which HIPAA elements are in place? (Select all that apply)',
        inputType: 'checklist',
        options: [
          { value: 'notice', label: 'Notice of Privacy Practices provided to consumers' },
          { value: 'training', label: 'HIPAA training for all staff' },
          { value: 'agreements', label: 'Business Associate Agreements' },
          { value: 'officer', label: 'Designated Privacy Officer' },
          { value: 'breach', label: 'Breach notification procedures' },
        ],
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'yes' },
        { condition: 'countAtLeast', questionId: 'q2', value: 4 },
      ], result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q1', value: 'yes', result: 'AT_RISK' },
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'HIPAA privacy program meets federal requirements.',
      atRisk: 'HIPAA program may be incomplete. Ensure all required elements are in place.',
      nonCompliant: 'No HIPAA privacy program. This violates federal law. Implement immediately.',
      unknown: 'Review HIPAA requirements.',
    },
    stateNotes: [
      'HIPAA Privacy Rule applies to covered entities per 45 CFR 164',
      'Notice of Privacy Practices required for all consumers',
      'HIPAA training required for all staff with access to PHI',
      'Business Associate Agreements required for third-party vendors',
      'Breach notification procedures must be documented',
    ],
  },

  {
    id: 'CO-G4',
    itemId: 'G4',
    stateCode: 'CO',
    title: 'Document Expiration Tracking',
    description: 'System to track license, certification, and clearance expirations',
    severity: 'high',
    regulationCode: '6 CCR 1011-1',
    regulationName: 'Record Maintenance',
    agencyName: 'Colorado Department of Public Health and Environment',
    agencyAbbr: 'CDPHE',
    agencyPhone: '(303) 692-2800',
    agencyUrl: 'https://cdphe.colorado.gov/home-care-agencies',
    questions: [
      {
        id: 'q1',
        question: 'Do you track expiration dates for all required documents? (Select all that apply)',
        inputType: 'checklist',
        options: [
          { value: 'license', label: 'Agency license (renewed annually)' },
          { value: 'insurance', label: 'Liability insurance' },
          { value: 'tb', label: 'TB screenings' },
          { value: 'cpr', label: 'CPR/First Aid certifications' },
          { value: 'dora', label: 'DORA professional licenses' },
          { value: 'training', label: 'Annual training due dates' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'Do you receive advance reminders before documents expire?',
        inputType: 'select',
        options: [
          { value: 'auto', label: 'Yes, automated reminders 30+ days in advance' },
          { value: 'manual', label: 'Yes, manual review monthly' },
          { value: 'no', label: 'No proactive tracking' },
        ],
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'countAtLeast', questionId: 'q1', value: 5 },
        { condition: 'includes', questionId: 'q2', value: ['auto', 'manual'] },
      ], result: 'COMPLIANT' },
      { condition: 'countAtLeast', questionId: 'q1', value: 3, result: 'AT_RISK' },
      { condition: 'countLessThan', questionId: 'q1', value: 3, result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Document expiration tracking is comprehensive with proactive reminders.',
      atRisk: 'Expiration tracking is incomplete. Implement tracking to prevent compliance lapses.',
      nonCompliant: 'No systematic document tracking in place. This creates significant compliance risk.',
      unknown: 'Implement document expiration tracking system.',
    },
    stateNotes: [
      'Agency license renewed annually per Part 4.1',
      'Track liability insurance, TB screenings, CPR/First Aid certifications',
      'DORA professional license verification for licensed staff',
      'Proactive reminders 30+ days before expiration recommended',
    ],
  },

  {
    id: 'CO-G5',
    itemId: 'G5',
    stateCode: 'CO',
    title: 'Audit Readiness and Survey Preparation',
    description: 'Preparedness for CDPHE surveys and audits',
    severity: 'medium',
    regulationCode: '6 CCR 1011-1',
    regulationName: 'Survey Readiness',
    agencyName: 'Colorado Department of Public Health and Environment',
    agencyAbbr: 'CDPHE',
    agencyPhone: '(303) 692-2800',
    agencyUrl: 'https://cdphe.colorado.gov/home-care-agencies',
    questions: [
      {
        id: 'q1',
        question: 'Are you prepared for an unannounced CDPHE survey?',
        inputType: 'select',
        options: [
          { value: 'yes', label: 'Yes, fully prepared' },
          { value: 'mostly', label: 'Mostly prepared, some gaps' },
          { value: 'no', label: 'Not prepared' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'Which survey preparation activities do you conduct? (Select all that apply)',
        inputType: 'checklist',
        options: [
          { value: 'self_audit', label: 'Regular self-audits' },
          { value: 'file_review', label: 'Personnel file reviews' },
          { value: 'consumer_review', label: 'Consumer record reviews' },
          { value: 'policy_review', label: 'Policy manual updates' },
          { value: 'mock_survey', label: 'Mock surveys' },
        ],
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'yes' },
        { condition: 'countAtLeast', questionId: 'q2', value: 3 },
      ], result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q1', value: 'mostly', result: 'AT_RISK' },
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Agency is well-prepared for CDPHE surveys.',
      atRisk: 'Some survey preparation gaps exist. Conduct regular self-audits to identify and correct deficiencies.',
      nonCompliant: 'Agency is not prepared for surveys. CDPHE can conduct unannounced visits. Implement survey preparation activities immediately.',
      unknown: 'Review survey preparation requirements.',
    },
    stateNotes: [
      'CDPHE survey frequency based on agency compliance history',
      'License fees include component reflecting planned survey status',
      'Civil fines up to $10,000 per calendar year for violations per Part 4.7',
      'Review CDPHE "Frequently Cited Deficiencies" guidance for common issues',
    ],
  },
];

export default COLORADO_QUESTIONS;